import React, {
  useState,
  useCallback,
  Fragment,
  useEffect,
  lazy,
  Suspense,
} from "react";
import {
  Grid2,
  Card,
  CardContent,
  Typography,
  TextField,
  ToggleButtonGroup,
  Paper,
  MenuItem,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { DropDownList } from "../../../../Utils/StaticData";
import Loading from "../../../../Controller/LoadingController/Loading";
import StreetDataTable from "./StreetDatatable";
import { LapsesChart } from "./LapsesChart";
import { AssetsChart } from "./AssertsChart";
import { RoutesChart } from "./RoutesChart";
import ToggleButton from "@mui/material/ToggleButton";
import moment from "moment/moment";
import SeverityCard from "./ServerityView";
import AppColors from "../../../../Utils/AppColor";
import {
  CtmLiveOverViewType,
  useCtmLiveOverView,
} from "../../../../ContextAPI/CtmLiveOverViewContext";
import { useTranslation } from "react-i18next";
import { GetSectorSeverity } from "../../../../ApiServices/ApiRequests";
import { AppConstants } from "../../../../Utils/AppConstants";
import { Margin } from "@mui/icons-material";

const OneMapComponent = React.lazy(() => import("./OneMap"));

// interface TimeRange {
//   label: string;
//   start: string;
//   end: string;
// }

const cardContentStyle = {
  paddingTop: `2px`,
  paddingBottom: 5,
  paddingLeft: `2px`,
  paddingRight: `2px`,
};

function CtMHomeComponent() {
  const theme = useTheme();

  const { ctmLiveOverViewState, dispatchCtmLiveOverView } =
    useCtmLiveOverView();
  const { t } = useTranslation();

  const handleInputChange = useCallback(
    (
      field: keyof CtmLiveOverViewType,
      value: string | boolean | number | null | number[][] | mapboxgl.Map
    ) => {
      dispatchCtmLiveOverView({ type: "SET_FIELD", field, value });
    },
    [dispatchCtmLiveOverView]
  );

  const handleBulkChange = useCallback(
    (value: Partial<CtmLiveOverViewType>) => {
      dispatchCtmLiveOverView({ type: "BULK_UPDATE", value });
    },
    [dispatchCtmLiveOverView]
  );
  // const [severityPercent, setSeverityPercent] = useState<SeverityPercent>({});
  // const [sectorChart, setSectorChart] = useState<Record<string, any>>({});
  //const [criticalCount, setCriticalCount] = useState<number>(0);
  // const [sensorDevices, setSensorDevices] = useState<SensorDevice[]>([]);
  // const [sensorCriticalCount, setSensorCriticalCount] = useState<number>(0);
  //const [selectODC, setSelectODC] = useState<string>("odcai_track2");

  // const timeRanges: TimeRange[] = [
  //   { label: "9PM-4AM", start: "21", end: "4" },
  //   { label: "4AM-11AM", start: "4", end: "11" },
  //   { label: "11AM-4PM", start: "11", end: "4" },
  //   { label: "4PM-9PM", start: "4", end: "21" },
  // ];

  const [loading, setLoading] = useState<boolean>(false);

  const timeVariable: number = parseFloat(
    `${moment().format("H")}.${moment().format("mm")}`
  );

  const getTimeRange = (): string => {
    if (timeVariable === 21 || timeVariable > 21 || timeVariable < 4.1) {
      return "1";
    } else if (timeVariable === 4 || timeVariable < 11) {
      return "2";
    } else if (timeVariable === 11 || timeVariable < 16) {
      return "3";
    } else {
      return "4";
    }
  };

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ): void => {
    handleInputChange("selectedTime", newAlignment);
    handleWebSocketChange(newAlignment);
  };

  const handleWebSocketChange = (newTimeRange: string): void => {
    console.log("newTimeRange", newTimeRange);
    console.log("getTimeRange()", getTimeRange());
    if (newTimeRange !== getTimeRange()) {
      handleInputChange("isWebSocketConneted", false);
      console.log("Disconnecting WebSocket");
    } else {
      handleInputChange("isWebSocketConneted", true);
      console.log("Connecting WebSocket");
    }
  };

  const control: {
    value: string;
    onChange: (
      _event: React.MouseEvent<HTMLElement>,
      newAlignment: string
    ) => void;
    exclusive: boolean;
  } = {
    value: ctmLiveOverViewState.selectedTime,
    onChange: handleChange,
    exclusive: true,
  };

  const Grid2StyleForCard = {
    backgroundColor: AppColors.purple,
    textAlign: `left` as const,
    color: AppColors.appWhite,
    fontSize: `7px`,
    paddingLeft: `5px`,
  };

  const handleODCChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange("SelectedODC", event.target.value);
  };

  useEffect(() => {
    retrieveData();
  }, []);

  const retrieveData = async () => {
    try {
      setLoading(true);
      const result = await GetSectorSeverity();

      var upadateValues = {
        SeverityPercent: {
          critical: result.criticalPercentage,
          moderate: result.moderatePercentage,
          clean: result.cleanPercentage,
        },
        CriticalCount: result.criticalCount,
      };

      handleBulkChange(upadateValues);
    } catch (e) {
      console.error("Error fetching  routes severity Count data:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid2 container spacing={1}>
      <Grid2 size={{ xs: 9 }}>
        <Grid2
          container
          sx={{ justifyContent: "space-between" }}
          alignItems="center"
        >
          {/* Breadcrumbs Section */}
          {/* <Grid2 size={{ xs: 12 }}>
            <Typography
              variant="h5"
              color="#838383"
              sx={{ marginLeft: "15px", marginTop: "15px" }}
            >
              {t("global.OnDemandCleaningOptimization")}
            </Typography>
          </Grid2> */}

          <Grid2 size={{ xs: 12, sm: 12, md: 12 }} mt={3.5}>
            {loading ? (
              <Loading loading={loading} type={`dots`} backDrop={false} />
            ) : (
              <SeverityCard />
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
            <Suspense
              fallback={
                <div>
                  <Loading loading={true} type={`dots`} backDrop={false} />
                </div>
              }
            >
              {/* Map and Time Toggle Container */}
              <Grid2
                sx={{
                  position: "relative", // Needed for proper absolute positioning inside
                  width: "100%",
                  overflow: "hidden", // Ensures content doesn't spill out
                }}
              >
                {/* Time Component */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "20px", // Distance from the top of the map
                    left: "20px", // Distance from the left of the map
                    zIndex: 1000, // Ensure it appears above the map
                    backgroundColor: "rgba(255, 255, 255, 0.8)", // Semi-transparent background
                    borderRadius: "8px",
                    padding: "8px",
                    boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  <ToggleButtonGroup
                    size="small"
                    {...control}
                    aria-label="Small sizes"
                  >
                    <ToggleButton value="1" key="1">
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "bold",
                          textTransform: "capitalize",
                          fontSize: 10,
                        }}
                      >
                        9 PM-4 AM
                      </Typography>
                    </ToggleButton>
                    <ToggleButton value="2" key="2">
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "bold",
                          textTransform: "capitalize",
                          fontSize: 10,
                        }}
                      >
                        4 AM-11 AM
                      </Typography>
                    </ToggleButton>
                    <ToggleButton value="3" key="3">
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "bold",
                          textTransform: "capitalize",
                          fontSize: 10,
                        }}
                      >
                        11 AM-4 PM
                      </Typography>
                    </ToggleButton>
                    <ToggleButton value="4" key="4">
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "bold",
                          textTransform: "capitalize",
                          fontSize: 10,
                        }}
                      >
                        4 PM-9 PM
                      </Typography>
                    </ToggleButton>
                    <ToggleButton value="5" key="5">
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "bold",
                          textTransform: "capitalize",
                          fontSize: 10,
                        }}
                      >
                        24 Hours
                      </Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Map Component */}
                <Card
                  sx={{
                    width: "100%",
                    padding: "10px",
                    // height: "100vh",
                    position: "relative",
                  }}
                >
                  <OneMapComponent />
                </Card>
              </Grid2>
            </Suspense>
          </Grid2>
        </Grid2>
      </Grid2>

      {/* Dropdowns Section */}

      <Grid2 size={{ xs: 3, sm: 3, md: 3 }}>
        <Grid2 container spacing={1}>
          <TextField
            sx={{ mt: 6.5 }}
            id="demo-simple-select-helper"
            value={ctmLiveOverViewState.SelectedODC}
            label="Select the vehicle"
            onChange={handleODCChange}
            size="small"
            select
            fullWidth
            // margin="normal"
          >
            {DropDownList.length > 0 &&
              DropDownList.map((x, i) => {
                return (
                  <MenuItem key={x.id} value={x.id}>
                    {x.name}
                  </MenuItem>
                );
              })}
          </TextField>

          <Card sx={{ padding: "5px" }}>
            <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
              <Grid2 container spacing={0.5}>
                <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                  <Card>
                    <CardContent style={cardContentStyle}>
                      {/* <CardCustomHeader title={`STREETS`} varient={'body2'} alignment={`center`}/>      */}
                      <Grid2 container spacing={0}>
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 12 }}
                          sx={Grid2StyleForCard}
                        >
                          <p>{t("global.STREETS")}</p>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                          <StreetDataTable />
                        </Grid2>
                      </Grid2>
                    </CardContent>
                  </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                  <Card>
                    <CardContent style={cardContentStyle}>
                      {/* <CardCustomHeader title={`STREETS`} varient={'body2'} alignment={`center`}/>      */}
                      <Grid2 container spacing={0}>
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 12 }}
                          sx={Grid2StyleForCard}
                        >
                          <p>
                            {ctmLiveOverViewState.SelectedODC ==
                            AppConstants.dicSensor
                              ? t("global.DICSENSORFEEDBACK")
                              : ctmLiveOverViewState.SelectedODC ==
                                AppConstants.smartCombi
                              ? t("global.SMARTCOMBIARMFEEDBACK")
                              : t("global.ON-DEMANDCLEANINGFEEDBACK")}
                          </p>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                          <Card
                            style={{
                              margin: 4,
                            }}
                          >
                            <Typography
                              variant="h4"
                              style={{ padding: "1px", textAlign: `center` }}
                            >
                              {ctmLiveOverViewState.SelectedODC ==
                              AppConstants.dicSensor
                                ? `${ctmLiveOverViewState.SensorCriticalCount}`
                                : ctmLiveOverViewState.SelectedODC ==
                                  AppConstants.smartCombi
                                ? `${"300/1000"}`
                                : `${ctmLiveOverViewState.CriticalCount}`}
                            </Typography>
                          </Card>
                        </Grid2>
                      </Grid2>
                    </CardContent>
                  </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                  <Card>
                    <CardContent style={cardContentStyle}>
                      {/*  <CardCustomHeader title={`ASSETS`} varient={'body2'} alignment={`center`}/>  */}
                      <Grid2 container spacing={0}>
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 12 }}
                          sx={Grid2StyleForCard}
                        >
                          <p>{t("global.ASSETS")}</p>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                          <AssetsChart />
                        </Grid2>
                      </Grid2>
                    </CardContent>
                  </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                  <Card>
                    <CardContent style={cardContentStyle}>
                      {/* <CardCustomHeader title={`LAPSES`} varient={'body2'} alignment={`center`}/> */}
                      <Grid2 container spacing={0}>
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 12 }}
                          sx={Grid2StyleForCard}
                        >
                          <p>{t("global.LAPSES")}</p>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                          <LapsesChart />
                        </Grid2>
                      </Grid2>
                    </CardContent>
                  </Card>
                </Grid2>
              </Grid2>
            </Grid2>
          </Card>
        </Grid2>
      </Grid2>

      <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
        <RoutesChart />
      </Grid2>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          textAlign: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "#000000",
            marginBottom: 1,
          }}
        >
          {t("global.copyRight")} © {moment().year()},{" "}
          {t("global.copyRightText")}
        </Typography>
      </Box>
    </Grid2>
  );
}

export default CtMHomeComponent;
